import React from 'react';
import { Prospect } from '../../shared/types';
import {
  ShoppingBag, MapPin, Heart, Link2, BarChart3,
  Smartphone, Star, CheckCircle2, Calendar, Package,
  Globe, Repeat, MessageSquare, TrendingUp, Zap
} from 'lucide-react';

interface EcosystemSectionProps {
  prospect: Prospect;
}

// Nicho detection by segment keyword (same logic as HeroSection)
const detectNicheKey = (prospect: Prospect): string => {
  const seg = (prospect.segment || '').toLowerCase();
  const demos = prospect.availableDemos || [];
  if (demos.includes('imobiliaria') || seg.includes('imobil')) return 'imobiliaria';
  if (demos.includes('clinica') || seg.includes('clínic') || seg.includes('saúde') || seg.includes('odontolog')) return 'clinica';
  if (demos.includes('gastronomia') || seg.includes('gastronom') || seg.includes('hambúrg') || seg.includes('restaurante')) return 'gastronomia';
  if (demos.includes('advocacia') || seg.includes('advocac') || seg.includes('jurídic')) return 'advocacia';
  if (demos.includes('automoveis') || seg.includes('automóvel') || seg.includes('veícul')) return 'automoveis';
  if (demos.includes('arquitetura') || seg.includes('arquitet') || seg.includes('interior')) return 'arquitetura';
  if (seg.includes('viagem') || seg.includes('viagens') || seg.includes('turism')) return 'viagens';
  if (seg.includes('academi') || seg.includes('fitness') || seg.includes('cross')) return 'fitness';
  return 'ecommerce';
};

const NICHE_CARDS: Record<string, {
  main: { title: string; desc: string; img: string };
  small: Array<{ icon: React.ElementType; title: string; desc: string; badge?: string }>;
  bottom: { title: string; desc: string; img: string };
}> = {
  ecommerce: {
    main: {
      title: 'Loja Virtual com 0% Taxa',
      desc: 'Venda sem intermediários. Catálogo, carrinho, Pix e pagamento integrado. Seus clientes compram no seu site, sem perder % por pedido.',
      img: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Link2, title: 'Biolink Pro', desc: 'Sua marca 100% no Instagram. Um link que vende.', badge: 'Alto Impacto' },
      { icon: MapPin, title: 'Google Meu Negócio', desc: 'Quem busca "moda feminina" encontra você primeiro.', badge: undefined },
      { icon: Package, title: 'Omnichannel', desc: 'Compre online, retire na loja ou receba em casa.', badge: undefined },
    ],
    bottom: {
      title: 'Programa de Recompensas & Indicações',
      desc: 'Clientes indicam clientes. Sistema de cashback e pontos que fideliza e reduz custo de aquisição.',
      img: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800&q=75&auto=format&fit=crop',
    },
  },
  viagens: {
    main: {
      title: 'Portal de Destinos & Pacotes',
      desc: 'Site próprio com catálogo de viagens, motor de busca de pacotes, orçamento online e integração direta com WhatsApp para fechamento.',
      img: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Calendar, title: 'Reserva Online', desc: 'Passageiro faz cotação e reserva 24h/dia sem ligar.', badge: 'Automação' },
      { icon: MapPin, title: 'Presença Google', desc: '"Agência de viagens [cidade]" — você no topo.', badge: undefined },
      { icon: MessageSquare, title: 'WhatsApp Ativo', desc: 'Funil automático de follow-up e confirmação.', badge: undefined },
    ],
    bottom: {
      title: 'CRM de Clientes & Histórico de Viagens',
      desc: 'Cada passageiro tem perfil, histórico e preferências. Campanhas direcionadas por destino e data comemorativa.',
      img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=75&auto=format&fit=crop',
    },
  },
  clinica: {
    main: {
      title: 'Agendamento Online 24h',
      desc: 'Paciente agenda pelo site, recebe confirmação automática por WhatsApp, lembrete D-1 e avaliação pós-consulta — tudo automático.',
      img: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Star, title: 'Reputação Google', desc: '4.9 ⭐ no Google Meu Negócio. Pacientes confiam.', badge: 'Credibilidade' },
      { icon: Smartphone, title: 'App do Paciente', desc: 'Exames, receitas e histórico no celular.', badge: undefined },
      { icon: CheckCircle2, title: 'Confirmação Auto', desc: 'Zero falha de agenda. Lembrete via WhatsApp.', badge: undefined },
    ],
    bottom: {
      title: 'Google & Presença Local Dominante',
      desc: 'Pacientes pesquisam "dentista perto de mim" e encontram você no Maps com fotos, avaliações e botão de agendamento direto.',
      img: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=75&auto=format&fit=crop',
    },
  },
  gastronomia: {
    main: {
      title: 'Cardápio PWA sem Taxa',
      desc: 'App próprio de delivery. Sem iFood, sem taxa por pedido. Seus clientes pedem no seu site, Pix cai direto na sua conta.',
      img: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: MapPin, title: 'Google Gastromap', desc: '"Hamburguer em [cidade]" — você no topo com fotos.', badge: 'Destaque' },
      { icon: Link2, title: 'Biolink do Restaurante', desc: 'Instagram → cardápio → pedido. Em 3 cliques.', badge: undefined },
      { icon: Calendar, title: 'Reserva de Mesa', desc: 'Clientes reservam online. Você gerencia no sistema.', badge: undefined },
    ],
    bottom: {
      title: 'Fidelidade & Programa de Pontos',
      desc: 'Clientes acumulam pontos a cada pedido. Cashback, brindes e datas especiais que trazem de volta — sem plataforma de terceiro.',
      img: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=75&auto=format&fit=crop',
    },
  },
  imobiliaria: {
    main: {
      title: 'Portal de Imóveis Premium',
      desc: 'Site com catálogo completo, tour virtual 360°, calculadora de financiamento e CRM de leads integrado. Alta conversão em qualquer tela.',
      img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: MapPin, title: 'Geolocalização', desc: '"Imóveis em [bairro]" — apareça no topo do Google.', badge: 'SEO Local' },
      { icon: Smartphone, title: 'App do Corretor', desc: 'Gerencie leads e agenda de visitas no celular.', badge: undefined },
      { icon: TrendingUp, title: 'Lead Nurturing', desc: 'Funil automático até a assinatura do contrato.', badge: undefined },
    ],
    bottom: {
      title: 'CRM & Automação de Relacionamento',
      desc: 'Cada lead recebe conteúdo personalizado pelo estágio: buscando, visitou, em negociação. Taxa de conversão até 3x maior.',
      img: 'https://images.unsplash.com/photo-1560184897-ae75f418493e?w=800&q=75&auto=format&fit=crop',
    },
  },
  advocacia: {
    main: {
      title: 'Site Institucional de Alta Autoridade',
      desc: 'Presença digital que transmite seriedade, expertise e segurança jurídica. Blog com artigos para SEO, formulários de consulta e CRM de leads.',
      img: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Globe, title: 'SEO Jurídico', desc: '"Advogado trabalhista [cidade]" — você no topo.', badge: 'Autoridade' },
      { icon: Calendar, title: 'Consulta Online', desc: 'Agendamento de consulta inicial pelo site, 24h.', badge: undefined },
      { icon: CheckCircle2, title: 'Portal do Cliente', desc: 'Acompanhe processos e documentos online.', badge: undefined },
    ],
    bottom: {
      title: 'Google Meu Negócio & Avaliações',
      desc: 'Avaliações 5 estrelas no Google aumentam a confiança e trazem clientes orgânicos. Gerenciamos e respondemos todas as avaliações.',
      img: 'https://images.unsplash.com/photo-1589578527966-fdac0f44566c?w=800&q=75&auto=format&fit=crop',
    },
  },
  fitness: {
    main: {
      title: 'App de Treino & Matrícula Online',
      desc: 'Aluno acessa treinos, agenda aulas e faz matrícula pelo app. Notificações de treino, desafios mensais e ranking motivam a consistência.',
      img: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Zap, title: 'Matrícula Online', desc: 'Aluno matricula e paga pelo celular, sem fila.', badge: 'Conversão' },
      { icon: Star, title: 'Google Maps', desc: '"Academia CrossFit [cidade]" — você no topo.', badge: undefined },
      { icon: Heart, title: 'Comunidade', desc: 'Grupo exclusivo + desafios mensais de engajamento.', badge: undefined },
    ],
    bottom: {
      title: 'Retenção com Programas de Desafio',
      desc: 'Alunos que participam de desafios mensais têm 68% menos cancelamentos. Gamificação que fideliza a longo prazo.',
      img: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=75&auto=format&fit=crop',
    },
  },
  automoveis: {
    main: {
      title: 'Showroom Digital com Estoque ao Vivo',
      desc: 'Catálogo online com fotos profissionais, vídeos 360°, ficha técnica completa e simulação de financiamento. Leads qualificados direto para o WhatsApp.',
      img: 'https://images.unsplash.com/photo-1617814076367-b759c7d7e738?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Globe, title: 'Busca Orgânica', desc: '"Comprar [modelo] [cidade]" — você no topo.', badge: 'SEO' },
      { icon: BarChart3, title: 'CRM de Leads', desc: 'Funil de interessado até a assinatura do contrato.', badge: undefined },
      { icon: Smartphone, title: 'App do Vendedor', desc: 'Gestão do estoque e leads no celular.', badge: undefined },
    ],
    bottom: {
      title: 'Programa de Indicação & Fidelização',
      desc: 'Cliente que indicou recebe cashback no próximo serviço. A melhor rede de vendas é a sua própria base satisfeita.',
      img: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=75&auto=format&fit=crop',
    },
  },
  arquitetura: {
    main: {
      title: 'Portfólio Editorial de Alto Padrão',
      desc: 'Site com projetos apresentados como editorial de design: fotografia profissional, stories do processo, ficha técnica e depoimento do cliente.',
      img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=75&auto=format&fit=crop',
    },
    small: [
      { icon: Globe, title: 'SEO por Estilo', desc: '"Arquiteto minimalista [cidade]" — topo do Google.', badge: 'Autoridade' },
      { icon: Calendar, title: 'Briefing Online', desc: 'Cliente preenche brief antes da primeira reunião.', badge: undefined },
      { icon: BarChart3, title: 'CRM de Projetos', desc: 'Gestão de cronograma e aprovações online.', badge: undefined },
    ],
    bottom: {
      title: 'Instagram como Galeria de Autoridade',
      desc: 'Feed curado com antes/depois, making-of e resultados. Cada post vira um argumento de venda — integrado ao portfólio online.',
      img: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=75&auto=format&fit=crop',
    },
  },
};

export const EcosystemSection: React.FC<EcosystemSectionProps> = ({ prospect }) => {
  const primaryColor = prospect.theme?.primary_color || '#0061FF';
  const nicheKey = detectNicheKey(prospect);
  const cards = NICHE_CARDS[nicheKey] || NICHE_CARDS.ecommerce;

  return (
    <section className="py-24 bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <div className="mb-14 max-w-2xl">
          <span
            className="text-xs font-black uppercase tracking-widest block mb-3"
            style={{ color: primaryColor }}
          >
            O Ecossistema Completo
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-4">
            Uma plataforma integrada<br />
            para <span style={{ color: primaryColor }}>multiplicar</span> seus resultados.
          </h2>
          <p className="text-slate-500 text-lg font-normal leading-relaxed">
            Não apenas um site. Um ecossistema onde cada canal foi desenhado para gerar desejo, confiança e conversão para a {prospect.trade_name}.
          </p>
        </div>

        {/* BENTO GRID — always light */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* BIG CARD — Main feature, col-span-2, photo */}
          <div className="md:col-span-2 rounded-3xl overflow-hidden border border-slate-200/80 relative" style={{ minHeight: 380 }}>
            <img
              src={cards.main.img}
              alt={cards.main.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 z-10">
              <div
                className="inline-block px-3 py-1 rounded-full text-xs font-black text-white mb-3"
                style={{ backgroundColor: primaryColor }}
              >
                Solução Principal
              </div>
              <h3 className="text-2xl font-black text-white mb-2">{cards.main.title}</h3>
              <p className="text-white/80 text-sm font-normal leading-relaxed max-w-md">{cards.main.desc}</p>
            </div>
          </div>

          {/* SMALL CARDS COLUMN (col-span-1, 3 stacked) */}
          <div className="flex flex-col gap-4">
            {cards.small.map((card, i) => {
              const CardIcon = card.icon;
              return (
                <div
                  key={i}
                  className="flex-1 rounded-3xl border border-slate-200/80 bg-white p-6 flex flex-col justify-between"
                  style={{ minHeight: 110 }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
                    >
                      <CardIcon className="w-4.5 h-4.5" />
                    </div>
                    {card.badge && (
                      <span
                        className="text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full"
                        style={{ color: primaryColor, backgroundColor: `${primaryColor}12` }}
                      >
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <div>
                    <strong className="text-sm font-bold text-slate-900 block mb-1">{card.title}</strong>
                    <p className="text-xs text-slate-500 leading-snug">{card.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* BOTTOM WIDE CARD — community/retention feature */}
          <div className="md:col-span-3 rounded-3xl overflow-hidden border border-slate-200/80 relative" style={{ minHeight: 220 }}>
            <img
              src={cards.bottom.img}
              alt={cards.bottom.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
            <div className="absolute inset-0 flex items-center p-8 z-10">
              <div className="max-w-lg">
                <h3 className="text-2xl font-black text-white mb-2">{cards.bottom.title}</h3>
                <p className="text-white/80 text-sm font-normal leading-relaxed">{cards.bottom.desc}</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
